import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircleIcon, ExclamationCircleIcon, ExclamationTriangleIcon, InformationCircleIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { cn } from '@/lib/cn';

type Intent = 'success' | 'error' | 'warning' | 'info';

interface ToastInput {
  title?: string;
  body: string;
  intent?: Intent;
}

interface Toast extends Required<Pick<ToastInput, 'body'>> {
  id: number;
  title?: string;
  intent: Intent;
}

interface ToastContextValue {
  push: (toast: ToastInput) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

interface ToastProviderProps {
  children: React.ReactNode;
}

export function ToastProvider(props: ToastProviderProps) {
  const { children } = props;
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((input: ToastInput) => {
    const id = Date.now() + Math.random();
    const intent = input.intent ?? 'success';
    setToasts((prev) => [...prev, { id, title: input.title, body: input.body, intent }]);
    const ttl = intent === 'error' ? 6_000 : 3_000;
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, ttl);
  }, []);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ push }}>
      {children}
      {createPortal(
        <div className="pointer-events-none fixed top-4 right-4 z-50 flex w-80 flex-col gap-2">
          {toasts.map((t) => (
            <ToastCard key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

const INTENT_BG: Record<Intent, string> = {
  success: 'bg-white border-green-200',
  error: 'bg-white border-red-200',
  warning: 'bg-white border-amber-200',
  info: 'bg-white border-blue-200',
};

const INTENT_ICON_COLOR: Record<Intent, string> = {
  success: 'text-green-600',
  error: 'text-red-600',
  warning: 'text-amber-600',
  info: 'text-blue-600',
};

const INTENT_ICON: Record<Intent, React.ReactNode> = {
  success: <CheckCircleIcon className="h-5 w-5" />,
  error: <ExclamationCircleIcon className="h-5 w-5" />,
  warning: <ExclamationTriangleIcon className="h-5 w-5" />,
  info: <InformationCircleIcon className="h-5 w-5" />,
};

interface ToastCardProps {
  toast: Toast;
  onDismiss: () => void;
}

function ToastCard(props: ToastCardProps) {
  const { toast, onDismiss } = props;
  return (
    <div
      role="status"
      className={cn(
        'pointer-events-auto flex items-start gap-3 rounded-lg border bg-white p-3 shadow-lg animate-in slide-in-from-right',
        INTENT_BG[toast.intent],
      )}
    >
      <span className={cn('mt-0.5', INTENT_ICON_COLOR[toast.intent])}>
        {INTENT_ICON[toast.intent]}
      </span>
      <div className="flex-1 min-w-0">
        {toast.title && <p className="text-sm font-semibold text-brand-900">{toast.title}</p>}
        <p className="text-sm text-brand-700 break-words">{toast.body}</p>
      </div>
      <button
        onClick={onDismiss}
        className="-mr-1 -mt-1 rounded p-1 text-brand-400 hover:bg-brand-100 hover:text-brand-600"
        aria-label="Dismiss"
      >
        <XMarkIcon className="h-4 w-4" />
      </button>
    </div>
  );
}

export function useToast(): (toast: ToastInput) => void {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx.push;
}
